import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { DataResult, DataSourceRequestState } from '@progress/kendo-data-query';
import React, { ChangeEvent, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  DeleteButton
} from 'src/components/shared/Button/Button';
import { LoadingPanel } from 'src/components/shared/DataGrid/LoadingPanel';
import { Icon } from 'src/components/shared/Icon/Icon';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { LabelType } from 'src/components/shared/Typography/LabelType';
import { SpecificationDto } from 'src/store/specifications/specification';
import { formatDate } from 'src/utils/date';
import { DetailsList } from '../Products/ProductsListMobile';

type Props = {
  data: DataResult;
  loading: boolean;
  requestState: DataSourceRequestState;
  onEdit: (specification: SpecificationDto) => void;
  onDelete: (specification: SpecificationDto) => void;
  onPageChange: (skip: number, take: number) => void;
};

export const SpecificationListMobile: React.FC<Props> = ({
  data,
  loading,
  requestState,
  onEdit,
  onDelete,
  onPageChange
}) => {
  const { t } = useTranslation();

  return (
    <Fragment>
      <Pagination
        count={Math.ceil(data.total / (requestState.take ?? 20))}
        siblingCount={1}
        onChange={(event: ChangeEvent<unknown>, page: number) => {
          const take = requestState.take ?? 20;
          onPageChange(page * take, take);
        }}
        variant="outlined"
        color="primary"
        className="mb-24"
      />

      {loading && <LoadingPanel />}

      {data.data.map((specification: SpecificationDto) => (
        <Accordion key={specification.id}>
          <AccordionSummary expandIcon={<Icon type="la-angle-down" />}>
            <Box display="flex" alignItems="center" width="100%">
              <Grid container spacing={0}>
                <Grid item xs={5}>
                  <LabelType>{t('props.number')}</LabelType>
                  <BodyType>{specification.no}</BodyType>
                </Grid>
                <Grid item xs={7}>
                  <LabelType>{t('props.name')}</LabelType>
                  <BodyType>{specification.name}</BodyType>
                </Grid>
              </Grid>
            </Box>
          </AccordionSummary>
          <AccordionDetails style={{ display: 'block' }} className="pr-48">
            <Grid container spacing={0}>
              <Grid item xs={5}>
                <DetailsList>
                  <li>
                    <LabelType>{t('props.date')}</LabelType>
                    <BodyType>
                      {formatDate(specification.regDate) ?? '–'}
                    </BodyType>
                  </li>
                  <li>
                    <LabelType>{t('segment.singular')}</LabelType>
                    <BodyType>{specification.segmentName ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('grade.singular')}</LabelType>
                    <BodyType>{specification.gradeName}</BodyType>
                  </li>
                </DetailsList>
              </Grid>

              <Grid item xs={7}>
                <DetailsList>
                  <li>
                    <LabelType>{t('packaging.singular')}</LabelType>
                    <BodyType>{specification.customerName ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('packaging.singular')}</LabelType>
                    <BodyType>{specification.packagingName ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('pallet.singular')}</LabelType>
                    <BodyType>{specification.palletName ?? '–'}</BodyType>
                  </li>
                </DetailsList>
              </Grid>
            </Grid>

            <hr />

            <Box display="flex">
              <ActionButton
                icon="la la-pen"
                onClick={() => onEdit(specification)}
                className="mr-16"
              />

              <DeleteButton onClick={() => onDelete(specification)} />
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Fragment>
  );
};
